import type {
  CreateBuiltInInstrumentInput,
  IBuiltInInstrumentDetail,
  IQuestionBankQuestionPayload,
} from '@brushfire/types'
import type {
  InstrumentDraftQuestion,
  InstrumentDraftQuestionType,
} from '@/features/instruments/infra/instrumentDraft.api'
import { newObjectIdHex } from '@/features/instruments/domain/objectIdHex'
import { mapBuilderQuestionsToInstrumentDraft } from '@/features/instruments/question-builder/mapBuilderToInstrumentDraft'
import { mapInstrumentDraftToBuilderQuestions } from '@/features/instruments/question-builder/mapInstrumentDraftToBuilder'
import type { BuilderQuestion } from '@/features/instruments/question-builder/types'

function draftRowToBankPayload(
  draft: ReturnType<typeof mapBuilderQuestionsToInstrumentDraft>[number],
  order: number,
): IQuestionBankQuestionPayload {
  const { id: _id, order: _order, type, prompt, fromTemplate: _ft, questionBankEntryId: _qb, ...rest } =
    draft
  return {
    order,
    type,
    prompt,
    ...rest,
  }
}

/** Maps builder state to POST `/built-in-instruments` body. */
export function builderQuestionsToBuiltInPayload(
  name: string,
  questions: BuilderQuestion[],
  estCompletionSecs: number,
): CreateBuiltInInstrumentInput {
  const draftRows = mapBuilderQuestionsToInstrumentDraft(questions)
  const payloads = draftRows.map((row, index) => draftRowToBankPayload(row, index))
  return {
    name: name.trim(),
    questions: payloads,
    questionCount: questions.length,
    estCompletionSecs,
  }
}

/** Stable fingerprint for “unchanged since last Add to Library” disable logic. */
export function builtInInstrumentLibrarySnapshotFingerprint(
  name: string,
  questions: BuilderQuestion[],
  estCompletionSecs: number,
): string | null {
  try {
    return JSON.stringify(builderQuestionsToBuiltInPayload(name, questions, estCompletionSecs))
  } catch {
    return null
  }
}

function bankPayloadToDraftRow(
  payload: IQuestionBankQuestionPayload,
  type: string,
  prompt: string,
  id = newObjectIdHex(),
): InstrumentDraftQuestion {
  const apiType = type as InstrumentDraftQuestionType
  const rawOptions = payload.options as
    | Array<{ id?: string; label: string; imageUrl?: string }>
    | undefined
  const options = rawOptions?.map((o) => ({
    id: o.id && /^[a-f0-9]{24}$/i.test(o.id) ? o.id : newObjectIdHex(),
    label: o.label,
    ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
  }))

  return {
    id,
    order: payload.order ?? 0,
    type: apiType,
    prompt,
    description: payload.description ?? null,
    mandatory: payload.mandatory ?? false,
    ...(options ? { options } : {}),
    minSelections: payload.minSelections,
    maxSelections: payload.maxSelections,
    yesLabel: payload.yesLabel,
    noLabel: payload.noLabel,
    showSelectAll: payload.showSelectAll,
    selectAllLabel: payload.selectAllLabel,
    randomizeOrder: payload.randomizeOrder,
    layoutHorizontal: payload.layoutHorizontal,
    multiColumn: payload.multiColumn,
    yesNoToggleStyle: payload.yesNoToggleStyle,
    hasOtherOption: payload.hasOtherOption,
    hasNoneOfAboveOption: payload.hasNoneOfAboveOption,
    minLength: payload.minLength,
    maxLength: payload.maxLength,
    placeholder: payload.placeholder,
    imageSize: payload.imageSize,
    earliestSelectableDate: payload.earliestSelectableDate,
    latestSelectableDate: payload.latestSelectableDate,
    disablePastDates: payload.disablePastDates,
    disableFutureDates: payload.disableFutureDates,
    dateInputFormat: payload.dateInputFormat,
    ratingScaleMax: payload.ratingScaleMax,
    ratingDisplayStyle: payload.ratingDisplayStyle,
    ratingLowLabel: payload.ratingLowLabel,
    ratingHighLabel: payload.ratingHighLabel,
    likertPointCount: payload.likertPointCount,
    likertLabels: payload.likertLabels,
    likertLayoutHorizontal: payload.likertLayoutHorizontal,
    sliderMinValue: payload.sliderMinValue,
    sliderMaxValue: payload.sliderMaxValue,
    sliderStep: payload.sliderStep,
    sliderLowLabel: payload.sliderLowLabel,
    sliderHighLabel: payload.sliderHighLabel,
  }
}

export function builtInQuestionsToPreviewRows(detail: IBuiltInInstrumentDetail) {
  const sorted = [...detail.questions].sort((a, b) => a.order - b.order)
  const draftRows = sorted.map((q) =>
    bankPayloadToDraftRow(q, q.type, q.prompt, newObjectIdHex()),
  )
  const builderQs = mapInstrumentDraftToBuilderQuestions(
    draftRows.map((row, index) => ({ ...row, order: index })),
  )
  return builderQs.map((builderQuestion, index) => ({
    builderQuestion,
    apiType: sorted[index]?.type ?? 'multiple_choice',
    prompt: sorted[index]?.prompt ?? builderQuestion.questionText,
  }))
}
